import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatError, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { FilePdf, ChartBar, Plus, Trash, CurrencyEur } from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

export default function Reportes() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [funcionarios, setFuncionarios] = useState([]);
  const [funcId, setFuncId] = useState("all");
  const [statusFilter, setStatusFilter] = useState(isAdmin ? "aprovado" : "pendente");
  const [desde, setDesde] = useState(() => {
    const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0];
  });
  const [hasta, setHasta] = useState(new Date().toISOString().split("T")[0]);
  const [resumo, setResumo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adelOpen, setAdelOpen] = useState(false);
  const [adelForm, setAdelForm] = useState({
    funcionario_id: "",
    fecha: new Date().toISOString().split("T")[0],
    monto: 0,
    descripcion: "",
  });

  useEffect(() => {
    if (isAdmin) api.get("/funcionarios").then((r) => setFuncionarios(r.data));
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = { desde, hasta, status: statusFilter };
      if (isAdmin && funcId && funcId !== "all") params.funcionario_id = funcId;
      const res = await api.get("/relatorios/resumo", { params });
      setResumo(res.data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const params = new URLSearchParams({ desde, hasta });
      if (isAdmin && funcId && funcId !== "all") params.append("funcionario_id", funcId);
      const token = localStorage.getItem("modolo_token");
      const res = await fetch(`${API}/relatorios/pdf?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("PDF failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `informe_modolo_${desde}_${hasta}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t("common.error"));
    }
  };

  const grandTotal = resumo.reduce((acc, r) => acc + (r.total_pagar || 0), 0);
  const grandAdel = resumo.reduce((acc, r) => acc + (r.total_adelantos || 0), 0);
  const grandNeto = resumo.reduce((acc, r) => acc + (r.total_neto ?? r.total_pagar ?? 0), 0);

  const saveAdelanto = async (e) => {
    e.preventDefault();
    try {
      await api.post("/adelantos", { ...adelForm, monto: parseFloat(adelForm.monto) || 0 });
      toast.success(t("common.success"));
      setAdelOpen(false);
      setAdelForm({ funcionario_id: "", fecha: new Date().toISOString().split("T")[0], monto: 0, descripcion: "" });
      loadReport();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const deleteAdelanto = async (id) => {
    if (!window.confirm(t("horas.confirmDelete"))) return;
    try {
      await api.delete(`/adelantos/${id}`);
      toast.success(t("common.success"));
      loadReport();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="reportes-page" className="overflow-x-hidden">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("reportes.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("reportes.subtitle")}</p>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            <Dialog open={adelOpen} onOpenChange={setAdelOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="add-adelanto-btn">
                  <Plus size={16} weight="bold" /> {t("reportes.newAdel")}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>{t("reportes.newAdel")}</DialogTitle>
                </DialogHeader>
                <form onSubmit={saveAdelanto} className="space-y-3">
                  <div>
                    <Label>{t("reportes.employee")}</Label>
                    <Select value={adelForm.funcionario_id} onValueChange={(v) => setAdelForm({ ...adelForm, funcionario_id: v })}>
                      <SelectTrigger data-testid="adel-emp-select"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {funcionarios.filter((f) => f.role === "funcionario").map((f) => (
                          <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>{t("reportes.adelDate")}</Label>
                      <Input type="date" value={adelForm.fecha} onChange={(e) => setAdelForm({ ...adelForm, fecha: e.target.value })} required data-testid="adel-date-input" />
                    </div>
                    <div>
                      <Label>{t("reportes.adelAmount")}</Label>
                      <Input type="number" step="0.01" value={adelForm.monto} onChange={(e) => setAdelForm({ ...adelForm, monto: e.target.value })} required data-testid="adel-amount-input" />
                    </div>
                  </div>
                  <div>
                    <Label>{t("reportes.adelDesc")}</Label>
                    <Textarea value={adelForm.descripcion} onChange={(e) => setAdelForm({ ...adelForm, descripcion: e.target.value })} rows={2} />
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setAdelOpen(false)}>{t("common.cancel")}</Button>
                    <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="adel-save-btn">{t("common.confirm")}</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
            <Button onClick={downloadPdf} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="export-pdf-btn">
              <FilePdf size={18} weight="bold" /> {t("reportes.exportPdf")}
            </Button>
          </div>
        )}
      </div>

      <Card className="p-5 mb-6 border-slate-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <Label>{t("reportes.from")}</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} data-testid="report-from-input" />
          </div>
          <div>
            <Label>{t("reportes.to")}</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} data-testid="report-to-input" />
          </div>
          <div>
            <Label>{t("horas.status")}</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="report-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="aprovado">{t("horas.approved")}</SelectItem>
                <SelectItem value="pendente">{t("horas.pending")}</SelectItem>
                <SelectItem value="pagada">{t("horas.paid")}</SelectItem>
                <SelectItem value="todos">{t("reportes.all")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {isAdmin && (
            <div>
              <Label>{t("reportes.employee")}</Label>
              <Select value={funcId} onValueChange={setFuncId}>
                <SelectTrigger data-testid="report-employee-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("reportes.all")}</SelectItem>
                  {funcionarios.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={loadReport} className="bg-green-600 hover:bg-green-700" data-testid="generate-report-btn">
            {t("reportes.generate")}
          </Button>
        </div>
      </Card>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : resumo.length === 0 ? (
        <Card className="p-12 text-center text-slate-400 border-slate-200">
          <ChartBar size={40} weight="duotone" className="mx-auto mb-3 text-slate-300" />
          {t("reportes.empty")}
        </Card>
      ) : (
        <div className="space-y-4">
          {resumo.map((r) => (
            <Card key={r.funcionario_id} className="p-6 border-slate-200 card-hover" data-testid={`report-row-${r.funcionario_id}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
                <div>
                  <h3 className="text-lg heading-font font-bold text-slate-900">{r.funcionario_nome}</h3>
                  <p className="text-sm text-slate-500">€ {r.valor_hora.toFixed(2)} / {t("reportes.hourRate").toLowerCase()}</p>
                </div>
                <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t("reportes.totalHours")}</div>
                    <div className="text-xl font-bold text-slate-900 heading-font">{r.total_horas.toFixed(2)} h</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{t("reportes.totalPay")}</div>
                    <div className="text-xl font-bold text-slate-900 heading-font">€ {r.total_pagar.toFixed(2)}</div>
                  </div>
                  {isAdmin && r.total_adelantos > 0 && (
                    <>
                      <div>
                        <div className="text-xs text-amber-700 uppercase tracking-wider font-semibold">{t("reportes.adelantos")}</div>
                        <div className="text-xl font-bold text-amber-700 heading-font">- € {r.total_adelantos.toFixed(2)}</div>
                      </div>
                      <div>
                        <div className={`text-xs uppercase tracking-wider font-semibold ${r.total_neto < 0 ? "text-red-700" : "text-green-700"}`}>{t("reportes.totalNeto")}</div>
                        <div className={`text-xl font-bold heading-font ${r.total_neto < 0 ? "text-red-700" : "text-green-700"}`}>€ {r.total_neto.toFixed(2)}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {isAdmin && r.adelantos && r.adelantos.length > 0 && (
                <div className="mt-3 pt-3 border-t border-amber-200 bg-amber-50/50 -mx-6 px-6 -mb-6 pb-4 rounded-b-lg">
                  <div className="text-xs font-bold uppercase tracking-wider text-amber-800 mb-2 flex items-center gap-1">
                    <CurrencyEur size={14} weight="duotone" /> {t("reportes.adelListTitle")}
                  </div>
                  <div className="space-y-1">
                    {r.adelantos.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-sm bg-white/60 rounded px-2 py-1">
                        <div className="flex-1 min-w-0">
                          <span className="text-slate-700">{a.fecha}</span>
                          {a.descripcion && <span className="text-slate-500"> · {a.descripcion}</span>}
                        </div>
                        <span className="font-semibold text-amber-700 mr-2">€ {a.monto.toFixed(2)}</span>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => deleteAdelanto(a.id)} data-testid={`delete-adel-${a.id}`}>
                          <Trash size={13} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          ))}

          {isAdmin && resumo.length > 0 && (
            <Card className="p-6 border-red-200 bg-red-50" data-testid="grand-total-card">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold uppercase tracking-wider text-slate-600">{t("reportes.totalPay")}</div>
                  <div className="text-lg heading-font font-semibold text-slate-900">€ {grandTotal.toFixed(2)}</div>
                </div>
                {grandAdel > 0 && (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-bold uppercase tracking-wider text-amber-700">{t("reportes.adelantos")}</div>
                      <div className="text-lg heading-font font-semibold text-amber-700">- € {grandAdel.toFixed(2)}</div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-red-300">
                      <div className="text-sm font-bold uppercase tracking-wider text-red-700">{t("reportes.grandTotalNeto")}</div>
                      <div className="text-2xl heading-font font-bold text-red-700">€ {grandNeto.toFixed(2)}</div>
                    </div>
                  </>
                )}
                {grandAdel === 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-red-300">
                    <div className="text-sm font-bold uppercase tracking-wider text-red-700">{t("reportes.grandTotal")}</div>
                    <div className="text-2xl heading-font font-bold text-red-700">€ {grandTotal.toFixed(2)}</div>
                  </div>
                )}
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
